import { useId, useMemo, useState, type FormEvent } from 'react';
import { Alert, Badge, Button, CloseButton, Form, OverlayTrigger, Spinner, Table, Tooltip } from 'react-bootstrap';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addStaffMember,
  grantStaffRole,
  listStaffMembers,
  removeStaffMember,
  removeStaffRole,
} from '@/apps/apis/mcpAccess';
import type { StaffAccessChange, StaffMember } from '@/apps/apis/mcpAccess/types';
import { errorMessage, formatLastChange, lastChangeDetail } from './mcpAccessFormat';
import McpSyncContent from './McpSyncContent';
import RolePicker from './RolePicker';
import type { SharedEmailFilter } from './sharedEmailFilter';
import { useMcpKeyAdministration } from './useMcpKeyAdministration';

const MEMBERS_QUERY_KEY = ['mcpAccess', 'members'] as const;
const PEOPLE_QUERY_KEY = ['mcpAccess', 'people'] as const;

/** One row's pick in the "add a role" list; empty until chosen. */
type RoleChoice = Record<string, string>;

/**
 * Who last changed a person and when. The full email and time, or why there is only a dash, open in a tooltip on
 * hover, focus or tap and are the accessible name. Omitted (`undefined`) means this Facade does not send the field
 * yet; `null` means it does and nothing was recorded.
 */
const LastChange = ({ change }: { change: StaffAccessChange | null | undefined }) => {
  const tooltipId = `last-change-${useId().replace(/[^a-z0-9]+/gi, '')}`;
  const detail = change
    ? lastChangeDetail(change)
    : change === undefined
      ? 'Not reported by the server yet'
      : 'No change recorded';
  return (
    <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{detail}</Tooltip>}>
      <span role="button" tabIndex={0} aria-label={detail} style={{ cursor: 'help' }}>
        {formatLastChange(change)}
      </span>
    </OverlayTrigger>
  );
};

/**
 * Who is staff and which team roles they hold, changed here instead of in the Logto console. HR managers and admins
 * add and remove people and change any team role; a team admin changes reader and editor roles of their team. Only
 * the roles the Facade offers are shown as changeable; everything else is listed for reference.
 */
const McpMembersContent = ({ filter: sharedFilter, onFilterChange }: SharedEmailFilter = {}) => {
  const queryClient = useQueryClient();
  const members = useQuery({ queryKey: MEMBERS_QUERY_KEY, queryFn: listStaffMembers });
  const administration = useMcpKeyAdministration();
  const [ownFilter, setOwnFilter] = useState('');
  const filter = sharedFilter ?? ownFilter;
  const setFilter = onFilterChange ?? setOwnFilter;
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('');
  const [roleChoice, setRoleChoice] = useState<RoleChoice>({});
  const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [changeError, setChangeError] = useState<string | null>(null);

  const term = filter.trim().toLowerCase();
  const shown = useMemo(
    () => (members.data?.members ?? []).filter((member) => member.email.toLowerCase().includes(term)),
    [members.data, term],
  );

  /** Runs one change, says what happened, and re-reads the list whether it worked or not. */
  const change = async (action: () => Promise<string>, failure: string) => {
    setBusy(true);
    setNotice(null);
    setChangeError(null);
    try {
      setNotice(await action());
    } catch (error) {
      setChangeError(errorMessage(error, failure));
    } finally {
      setBusy(false);
      void queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: PEOPLE_QUERY_KEY });
    }
  };

  const addPerson = (event: FormEvent) => {
    event.preventDefault();
    const email = newEmail.trim().toLowerCase();
    void change(async () => {
      await addStaffMember(email, newRole);
      setNewEmail('');
      setNewRole('');
      return `Added ${email} with ${newRole}. They can sign in with Google now.`;
    }, `Could not add ${email}.`);
  };

  const grant = (member: StaffMember) => {
    const role = roleChoice[member.email];
    if (!role || !members.data?.grantableRoles.includes(role)) {
      return;
    }
    void change(async () => {
      await grantStaffRole(member.email, role);
      setRoleChoice((current) => ({ ...current, [member.email]: '' }));
      return `Gave ${role} to ${member.email}.`;
    }, 'Could not grant the role.');
  };

  const takeAway = (member: StaffMember, role: string) =>
    void change(async () => {
      await removeStaffRole(member.email, role);
      return `Removed ${role} from ${member.email}.`;
    }, 'Could not remove the role.');

  const removePerson = (email: string) => {
    setPendingRemoval(null);
    void change(async () => {
      const { revokedKeyCount } = await removeStaffMember(email);
      return `Removed ${email} from staff and revoked ${revokedKeyCount} MCP ${revokedKeyCount === 1 ? 'key' : 'keys'}.`;
    }, `Could not remove ${email}.`);
  };

  if (members.isPending) {
    return <Spinner animation="border" size="sm" />;
  }
  if (members.isError) {
    return <Alert variant="danger">{errorMessage(members.error, 'Could not load the staff list.')}</Alert>;
  }

  const { grantableRoles, canAddPeople } = members.data;
  const newRoleIsGrantable = grantableRoles.includes(newRole);

  return (
    <>
      <h2 className="h4 mb-1">People and team roles</h2>
      <p className="text-muted">
        A team role decides which MCP tools someone gets. Changes take effect on their next key; every change is
        recorded with your email. Company-wide roles are changed in Logto.
      </p>

      {canAddPeople && (
        <Form className="d-flex flex-wrap gap-2 align-items-center mb-3" onSubmit={addPerson}>
          <Form.Control
            aria-label="New person's email"
            type="email"
            placeholder="new.person@skin-seoul.com"
            value={newEmail}
            onChange={(event) => setNewEmail(event.target.value)}
            style={{ maxWidth: 280 }}
            required
          />
          <RolePicker
            ariaLabel="First team role"
            placeholder="Team role…"
            options={grantableRoles}
            value={newRole}
            onChange={setNewRole}
            style={{ width: 260 }}
          />
          <Button type="submit" disabled={busy || !newEmail.trim() || !newRoleIsGrantable}>
            Add person
          </Button>
        </Form>
      )}

      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <Form.Control
          aria-label="Filter by email"
          placeholder="Filter by email"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          style={{ maxWidth: 320 }}
        />
        <span className="text-muted small">{shown.length} people</span>
      </div>

      {changeError && <Alert variant="danger">{changeError}</Alert>}
      {notice && <Alert variant="success">{notice}</Alert>}

      {shown.length === 0 ? (
        <Alert variant="secondary">{term ? `No one matches "${filter.trim()}".` : 'No one to show yet.'}</Alert>
      ) : (
        <Table size="sm" hover responsive>
          <thead>
            <tr>
              <th>Email</th>
              <th>Roles</th>
              <th>Add a role</th>
              {canAddPeople && (
                <th>
                  <span className="visually-hidden">Actions</span>
                </th>
              )}
              <th>Last changed</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((member) => {
              const addable = grantableRoles.filter((role) => !member.roleNames.includes(role));
              const choice = roleChoice[member.email] ?? '';
              return (
                <tr key={member.email}>
                  <td>{member.email}</td>
                  <td>
                    <span className="d-inline-flex flex-wrap gap-1">
                      {member.roleNames.length === 0 && <span className="text-muted">No roles</span>}
                      {member.roleNames.map((role) =>
                        grantableRoles.includes(role) ? (
                          <Badge key={role} bg="primary" className="d-inline-flex align-items-center gap-1">
                            {role}
                            <CloseButton
                              variant="white"
                              aria-label={`Remove ${role} from ${member.email}`}
                              disabled={busy}
                              onClick={() => takeAway(member, role)}
                              style={{ fontSize: '0.5rem' }}
                            />
                          </Badge>
                        ) : (
                          <Badge key={role} bg="secondary" title="Changed in Logto">
                            {role}
                          </Badge>
                        ),
                      )}
                    </span>
                  </td>
                  <td>
                    {addable.length > 0 && (
                      <span className="d-inline-flex gap-1">
                        <RolePicker
                          size="sm"
                          ariaLabel={`Role to give ${member.email}`}
                          placeholder="Choose…"
                          options={addable}
                          value={choice}
                          onChange={(role) => setRoleChoice((current) => ({ ...current, [member.email]: role }))}
                          style={{ width: 220 }}
                        />
                        <Button
                          size="sm"
                          variant="outline-primary"
                          disabled={busy || !addable.includes(choice)}
                          onClick={() => grant(member)}
                        >
                          Give
                        </Button>
                      </span>
                    )}
                  </td>
                  {canAddPeople && (
                    <td className="text-end text-nowrap">
                      {pendingRemoval === member.email ? (
                        <span className="d-inline-flex gap-2">
                          <Button size="sm" variant="danger" disabled={busy} onClick={() => removePerson(member.email)}>
                            Confirm remove
                          </Button>
                          <Button size="sm" variant="outline-secondary" onClick={() => setPendingRemoval(null)}>
                            Cancel
                          </Button>
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          disabled={busy}
                          title="Removes them from staff and revokes all their MCP keys"
                          onClick={() => setPendingRemoval(member.email)}
                        >
                          Remove from staff
                        </Button>
                      )}
                    </td>
                  )}
                  <td className="text-nowrap text-muted small">
                    <LastChange change={member.lastChange} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {administration.data?.companyAdmin && <McpSyncContent />}
    </>
  );
};

export default McpMembersContent;
